import React, { useEffect, useRef, useState } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import { Html5Qrcode } from "html5-qrcode";
import { rewardApi } from "../../api/rewardApi";
import logo from "../../assets/images/logo.png";
import "./reward-entry.scss";

const QR_REGION_ID = "reward-qr-reader";

const RewardEntryPage = () => {
    const scannerRef = useRef(null);

    const [invoiceNo, setInvoiceNo] = useState("");
    const [target, setTarget] = useState("");
    const [phone, setPhone] = useState("");
    const [amount, setAmount] = useState("");
    const [isScanned, setIsScanned] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [popup, setPopup] = useState({
        show: false,
        type: "",
        title: "",
        message: "",
    });

    useEffect(() => {
        startScanner();

        return () => {
            stopScanner();
        };
    }, []);

    const startScanner = async () => {
        if (scannerRef.current) return;

        const html5QrCode = new Html5Qrcode(QR_REGION_ID);
        scannerRef.current = html5QrCode;

        try {
            await html5QrCode.start(
                { facingMode: "environment" }, // 👈 force back camera
                {
                    fps: 10,
                    qrbox: { width: 260, height: 260 },
                },
                (decodedText) => {
                    handleQrSuccess(decodedText);
                },
                () => {}
            );
        } catch (err) {
            console.error("Camera start failed:", err);
        }
    };

    const stopScanner = async () => {
        if (!scannerRef.current) return;

        try {
            await scannerRef.current.stop();
            await scannerRef.current.clear();
        } catch (err) {}

        scannerRef.current = null;
    };

    const extractReceiptData = (text) => {
        try {
            const url = new URL(text);
            const parts = url.pathname.split("/").filter(Boolean);
            const invoiceIndex = parts.indexOf("einvoice");

            if (invoiceIndex === -1 || !parts[invoiceIndex + 1]) {
                return {
                    invoice_no: "",
                    target: "",
                };
            }

            return {
                invoice_no: parts[invoiceIndex + 1],
                target: url.searchParams.get("target") || "",
            };
        } catch (err) {
            return {
                invoice_no: "",
                target: "",
            };
        }
    };

    const handleQrSuccess = async (decodedText) => {
        const result = extractReceiptData(decodedText);

        if (!result.invoice_no) {
            showPopup({
                type: "error",
                title: "Invalid QR Code",
                message: "Please scan a valid receipt QR code.",
            });
            return;
        }

        setInvoiceNo(result.invoice_no);
        setTarget(result.target || "");
        setIsScanned(true);

        await stopScanner();
    };

    const showPopup = ({ type, title, message }) => {
        setPopup({
            show: true,
            type,
            title,
            message,
        });
    };

    const closePopup = () => {
        setPopup({
            show: false,
            type: "",
            title: "",
            message: "",
        });
    };

    const resetScanner = () => {
        setInvoiceNo("");
        setTarget("");
        setPhone("");
        setAmount("");
        setIsScanned(false);
        closePopup();

        setTimeout(() => {
            startScanner();
        }, 300);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!phone.trim()) {
            showPopup({
                type: "error",
                title: "Phone Number Required",
                message: "Please enter your phone number.",
            });
            return;
        }

        if (!amount.trim()) {
            showPopup({
                type: "error",
                title: "Receipt Amount Required",
                message: "Please enter your receipt amount.",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const data = await rewardApi.claimReceipt({
                invoice_no: invoiceNo,
                phone: phone.trim(),
                amount: amount.trim(),
                target,
            });

            showPopup({
                type: "success",
                title: "Receipt Bound Successfully",
                message: data.message || "Your receipt has been successfully bound.",
            });
        } catch (err) {
            showPopup({
                type: "error",
                title: "Binding Failed",
                message: err.message || "Unable to bind this receipt.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="reward-entry">
            <main className="reward-entry__card">
                <img src={logo} alt="Logo" className="reward-entry__logo" />

                <h1>Scan Receipt</h1>

                <p className="reward-entry__subtitle">
                    Scan your receipt QR code to bind it with your phone number.
                </p>

                {!isScanned && (
                    <div className="reward-entry__scanner">
                        <div id={QR_REGION_ID} />
                    </div>
                )}

                {isScanned && (
                    <form className="reward-entry__form" onSubmit={handleSubmit}>
                        <div className="reward-entry__summary">
                            <div>
                                <span>Invoice No.</span>
                                <strong>{invoiceNo}</strong>
                            </div>

                            {target && (
                                <div>
                                    <span>Target</span>
                                    <strong>{target}</strong>
                                </div>
                            )}
                        </div>

                        <div className="reward-entry__field">
                            <label>Phone Number</label>
                            <input
                                type="tel"
                                value={phone}
                                placeholder="Example: 0123456789"
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </div>

                        <button
                            type="submit"
                            className="reward-entry__button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>

                        <button
                            type="button"
                            className="reward-entry__link-button"
                            onClick={resetScanner}
                        >
                            Scan Again
                        </button>
                    </form>
                )}
            </main>

            {popup.show && (
                <div className="reward-entry__popup">
                    <div className="reward-entry__popup-card">
                        <div
                            className={`reward-entry__popup-icon reward-entry__popup-icon--${popup.type}`}
                        >
                            {popup.type === "success" ? "✓" : "!"}
                        </div>

                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>

                        <button
                            type="button"
                            onClick={popup.type === "success" ? resetScanner : closePopup}
                        >
                            {popup.type === "success" ? "Done" : "Close"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RewardEntryPage;